import { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ChevronRight, ArrowLeft, BookOpen, User as UserIcon } from 'lucide-react';
import { getArticleBySlug } from '../data/articles';

const setMetaDescription = (description) => {
  let meta = document.querySelector('meta[name="description"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'description');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', description);
};

export const ArticuloDetallePage = () => {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);

  useEffect(() => {
    if (!article) return;
    const prevTitle = document.title;
    const prevMeta = document.querySelector('meta[name="description"]')?.getAttribute('content') || '';
    document.title = `${article.title} | Rise & Shine Learning Zone`;
    setMetaDescription(article.metaDescription);
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      setMetaDescription(prevMeta);
    };
  }, [article]);

  if (!article) {
    return <Navigate to="/articulos" replace />;
  }

  return (
    <div className="pt-20" data-testid="articulo-detalle-page">
      {/* Breadcrumb */}
      <nav
        aria-label="Breadcrumb"
        className="bg-[#F3F0EA] border-b border-stone-200/70"
        data-testid="breadcrumb-nav"
      >
        <div className="max-w-4xl mx-auto px-6 sm:px-8 md:px-12 py-4">
          <ol className="flex items-center flex-wrap gap-1.5 text-sm text-stone-600">
            <li>
              <Link to="/" className="hover:text-[#43b6b8] transition-colors" data-testid="bc-inicio">
                Inicio
              </Link>
            </li>
            <li aria-hidden="true">
              <ChevronRight className="w-4 h-4 text-stone-400" />
            </li>
            <li>
              <Link to="/articulos" className="hover:text-[#43b6b8] transition-colors" data-testid="bc-articulos">
                Artículos
              </Link>
            </li>
            <li aria-hidden="true">
              <ChevronRight className="w-4 h-4 text-stone-400" />
            </li>
            <li
              className="text-stone-900 font-medium line-clamp-1"
              aria-current="page"
              data-testid="bc-current"
            >
              {article.title}
            </li>
          </ol>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-14 md:py-20 bg-gradient-to-br from-[#43b6b8] to-[#359a9c]">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 md:px-12">
          <Link
            to="/articulos"
            className="inline-flex items-center gap-2 text-white/90 hover:text-white text-sm font-medium mb-6 transition-colors"
            data-testid="back-to-articulos"
          >
            <ArrowLeft className="w-4 h-4" /> Volver a Artículos
          </Link>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6 leading-tight">
            {article.title}
          </h1>
          <div className="flex items-center flex-wrap gap-x-6 gap-y-2 text-white/90 text-sm">
            <span className="inline-flex items-center gap-2">
              <UserIcon className="w-4 h-4" /> Por {article.author}
            </span>
            <span className="inline-flex items-center gap-2">
              <BookOpen className="w-4 h-4" /> {article.date}
            </span>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="bg-[#F3F0EA] pb-2">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 md:px-12 -mt-10 md:-mt-14 relative z-10">
          <div className="rounded-2xl overflow-hidden shadow-[0_20px_50px_rgb(0,0,0,0.12)] aspect-[16/9] bg-stone-100">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-center"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Article Body */}
      <article className="bg-[#F3F0EA] pt-12 pb-20 md:pb-28">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 md:px-12">
          <div className="bg-white p-8 md:p-12 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <div className="prose-content space-y-6" data-testid="article-content">
              {article.sections.map((section, idx) => {
                const key = `${article.slug}-section-${idx}`;
                if (section.type === 'heading') {
                  return (
                    <h2
                      key={key}
                      className="text-xl sm:text-2xl font-semibold text-stone-900 mt-4 first:mt-0"
                    >
                      {section.text}
                    </h2>
                  );
                }
                if (section.type === 'list') {
                  return (
                    <ul key={key} className="space-y-2 pl-1">
                      {section.items.map((item) => (
                        <li
                          key={`${key}-${item}`}
                          className="flex items-start gap-3 text-base text-stone-700 leading-relaxed"
                        >
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#d3b163] flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  );
                }
                return (
                  <p
                    key={key}
                    className="text-base sm:text-[17px] text-stone-700 leading-relaxed"
                  >
                    {section.text}
                  </p>
                );
              })}
            </div>

            {/* Author byline */}
            {article.authorBio && (
              <div className="mt-10 pt-6 border-t border-stone-200">
                <p className="text-sm text-stone-600 italic">
                  <span className="font-semibold text-stone-800 not-italic">{article.author}</span>
                  {' — '}
                  {article.authorBio}
                </p>
              </div>
            )}
          </div>

          {/* Bottom navigation */}
          <div className="mt-10 flex justify-center">
            <Link
              to="/articulos"
              className="inline-flex items-center gap-2 bg-white text-stone-800 hover:text-[#43b6b8] px-6 py-3 rounded-full font-semibold border border-stone-200 hover:border-[#43b6b8]/40 transition-all shadow-sm"
              data-testid="back-to-articulos-bottom"
            >
              <ArrowLeft className="w-4 h-4" /> Volver a Artículos
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
};
